import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useEffectEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SCAEnrollmentState } from '#src/__generated__/globalTypes.ts';
import { LoadingButton } from '#src/atoms/buttons/LoadingButton/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';
import { StepWrapper } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { MangopayScaIframe } from '#src/components/wallet/MangopayScaIframe/index.tsx';
import { ORIGIN } from '#src/config.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { glossary } from '#src/lib/glossary.ts';

import type {
  GetCurrentUserScaStatusQuery,
  GetCurrentUserScaStatusQueryVariables,
  GetMangopayScaUrlQuery,
  GetMangopayScaUrlQueryVariables,
} from './__generated__/index.graphql.ts';

const Content = styled(Vertical).attrs({ gap: 2 })`
  height: 100%;
`;

type Props = {
  onSuccess: () => void;
  setStep: (step: CreateFiatWalletSteps) => void;
};

const List = styled.ul`
  padding-left: var(--double-unit);
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  margin: 0;

  li {
    list-style-type: disc;
  }
  ul {
    padding-left: var(--triple-unit);
    li {
      list-style-type: circle;
    }
  }
`;

const GET_SCA_URL_QUERY = gql`
  mutation GetMangopayScaUrlQuery($input: enrollSCAInput!) {
    enrollSCA(input: $input) {
      redirectUrl
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<GetMangopayScaUrlQuery, GetMangopayScaUrlQueryVariables>;

const GET_CURRENT_USER_SCA_STATUS_QUERY = gql`
  query GetCurrentUserScaStatusQuery {
    currentUser {
      slug
      mangopayUser {
        id
        scaEnrollmentState
      }
    }
  }
` as TypedDocumentNode<
  GetCurrentUserScaStatusQuery,
  GetCurrentUserScaStatusQueryVariables
>;

const usePollCurrentUserScaStatus = ({
  enabled,
  onSuccess,
  onFailed,
}: {
  enabled: boolean;
  onSuccess: () => void;
  onFailed?: () => void;
}) => {
  const { data } = useQuery(GET_CURRENT_USER_SCA_STATUS_QUERY, {
    pollInterval: enabled ? 1000 : 0,
    skip: !enabled,
  });

  const onFailedEvent = useEffectEvent(() => {
    onFailed?.();
  });

  useEffect(() => {
    if (!enabled) return () => {};
    const timeout = setTimeout(() => {
      onFailedEvent();
    }, 10000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  if (
    enabled &&
    data?.currentUser?.mangopayUser?.scaEnrollmentState ===
      SCAEnrollmentState.SCA_ENROLLED
  ) {
    onSuccess();
  }

  return null;
};

export const MangopayScaStep = ({ setStep, onSuccess }: Props) => {
  const { showNotification } = useSnackNotificationContext();

  const [getMangopayScaUrl, { loading }] = useMutation(GET_SCA_URL_QUERY, {
    showErrorsWithSnackNotification: true,
  });
  const [iframeUrl, setIFrameUrl] = useState<string | undefined>(undefined);
  const [polling, setPolling] = useState(false);

  const onClose = () => {
    setIFrameUrl(undefined);
  };

  const onPollSuccess = () => {
    setPolling(false);
    onSuccess();
  };

  const onIframeFlowSuccess = () => {
    setPolling(true);
  };

  const onStatusNotChanged = () => {
    showNotification('scaStatusNotChanged');
    setPolling(false);
    onClose();
  };

  usePollCurrentUserScaStatus({
    enabled: polling,
    onSuccess: onPollSuccess,
    onFailed: onStatusNotChanged,
  });

  const onClick = async () => {
    const result = await getMangopayScaUrl({
      variables: {
        input: {
          returnUrl: `${ORIGIN}/mangopay-sca.html`,
        },
      },
    });
    if (result.data?.enrollSCA?.redirectUrl) {
      setIFrameUrl(result.data.enrollSCA.redirectUrl);
    }
  };

  return (
    <>
      {!!iframeUrl && (
        <MangopayScaIframe
          open={!!iframeUrl}
          mangopayScaUrl={iframeUrl}
          onSuccess={onIframeFlowSuccess}
          onClose={onClose}
          onFailed={() => {
            showNotification('scaFailed');
            onClose();
          }}
        />
      )}
      <StepWrapper
        setStep={setStep}
        step={CreateFiatWalletSteps.MANGOPAY_SCA}
        submitButton={
          <LoadingButton onClick={onClick} size="medium" loading={loading}>
            <FormattedMessage {...glossary.continue} />
          </LoadingButton>
        }
      >
        <Content>
          <HeadlineL as="h3">
            <FormattedMessage
              id="createFiatWallet.MangopayScaStep.title"
              defaultMessage="Secure your Sorare account with Mangopay"
            />
          </HeadlineL>
          <BodyM>
            <FormattedMessage
              id="createFiatWallet.MangopayScaStep.description"
              defaultMessage="Complete a one-time Strong Customer Authentication (SCA) with our trusted payment partner, Mangopay."
            />
          </BodyM>
          <BodyM>
            <FormattedMessage
              id="createFiatWallet.MangopayScaStep.thisVerificationIs"
              defaultMessage="This verification is:"
            />
          </BodyM>
          <List>
            <li>
              <BodyM>
                <FormattedMessage
                  id="createFiatWallet.MangopayScaStep.description.mandatoryAndSecure"
                  defaultMessage="<strong>Mandatory and secure</strong> — it protects your account and your funds."
                  values={{
                    strong: Bold,
                  }}
                />
              </BodyM>
            </li>
            <li>
              <BodyM>
                <FormattedMessage
                  id="createFiatWallet.MangopayScaStep.description.quick"
                  defaultMessage="<strong>Quick (~1 min)</strong> — you’ll confirm just three things:"
                  values={{
                    strong: Bold,
                  }}
                />
              </BodyM>
            </li>
            <List>
              <li>
                <BodyM>
                  <FormattedMessage
                    id="createFiatWallet.MangopayScaStep.description.yourEmailAddress"
                    defaultMessage="<strong>Your email address</strong> (mandatory)"
                    values={{
                      strong: Bold,
                    }}
                  />
                </BodyM>
              </li>
              <li>
                <BodyM>
                  <FormattedMessage
                    id="createFiatWallet.MangopayScaStep.description.new6DigitPIN"
                    defaultMessage="<strong>A new 6-digit PIN</strong> (mandatory)"
                    values={{
                      strong: Bold,
                    }}
                  />
                </BodyM>
              </li>
              <li>
                <BodyM>
                  <FormattedMessage
                    id="createFiatWallet.MangopayScaStep.description.oneAdditionalMethodOfYourChoice"
                    defaultMessage="<strong>One additional method of your choice:</strong> either your phone number or a trusted device / security key"
                    values={{
                      strong: Bold,
                    }}
                  />
                </BodyM>
              </li>
            </List>
          </List>
        </Content>
      </StepWrapper>
    </>
  );
};
